<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Connections</h1>
        <p>Keys and tokens that let other software act as you.</p>
      </div>
    </header>

    <!-- The legacy API key. One per person, a FULL login -- not limited to a workspace or a
         scope -- so it is worth being blunt about what handing it over means. -->
    <section class="fts-settings-card">
      <div>
        <h2>API key</h2>
        <p class="fts-settings-card-sub">
          Signs in as you, with everything you can do, in every workspace you belong to. Only give
          it to software you trust.
        </p>
      </div>

      <!-- Masked until asked for: this is a full login, and a screen is easy to read over a
           shoulder. The old tab showed it in clear the moment it opened. -->
      <div class="fts-settings-key" v-if="key">
        <code>{{ revealed ? key : masked }}</code>
        <button type="button" class="fts-settings-btn" @click="revealed = !revealed">
          {{ revealed ? "Hide" : "Show" }}
        </button>
        <button type="button" class="fts-settings-btn" @click="copy">
          {{ copied ? "Copied" : "Copy" }}
        </button>
      </div>
      <p class="fts-settings-note" v-else>You have no API key.</p>

      <div class="fts-settings-row">
        <div class="fts-settings-row-text">
          <strong>{{ key ? "Replace this key" : "Generate a key" }}</strong>
          <span v-if="key">
            The current key is refused from then on — though it can take up to two hours to be
            refused everywhere, so replacing a leaked key does not close the leak at once.
          </span>
          <span v-else>Creates a key that signs in as you.</span>
        </div>
        <button type="button" class="fts-settings-btn" :disabled="generating" @click="generate">
          {{ generating ? "Generating…" : (key ? "Replace" : "Generate") }}
        </button>
      </div>
    </section>

    <!-- Personal access tokens: scoped, per-workspace, revocable one at a time. Still behind the
         same staff-only gate the /connections route has (the feature flag is off for customers). -->
    <section class="fts-settings-card" v-if="isInternal">
      <div>
        <h2>Personal access tokens</h2>
        <p class="fts-settings-card-sub">
          For the Inistate API and MCP. Each acts as you, limited to the scopes and workspaces you
          choose, and can be revoked on its own.
        </p>
      </div>
      <fts-connections embedded />
    </section>
  </div>
</template>

<script>
/**
 * Settings › Your account › Connections — the old profile modal's Integration tab (the legacy API
 * key) and the /connections page (personal access tokens), in one place because both are the
 * same kind of thing: a credential that acts as this person.
 *
 * THE KEY ENDPOINT ROTATES ON EVERY CALL. `GET api/user/GenerateAPIKey` makes a new AppId/AppKey
 * and overwrites the old pair. So this page never calls it to SHOW a key -- the current one
 * arrives with the profile (`ProfileController` returns `APIKey`) -- and only calls it behind an
 * explicit Replace and a confirmation that says what it breaks.
 *
 * Rotation is not instant, and the page says so: the fsk filter caches a principal for two hours
 * keyed by the old AppId and serves cache hits without re-checking, so a replaced key keeps
 * working up to 2h per app server. Someone rotating because a key leaked needs to know that.
 *
 * These are the person's, not the workspace's: a workspace admin's view of what reaches THEIR
 * data is a different page ("App access", proposal §4 #14), and revoking there can only narrow a
 * grant, because a connection is owned by a person and can span workspaces.
 */
import { authorizationStore } from "../../store/authorizationStore";
import FTSConnections from "../../client/FTSConnections";

export default {
  name: "SettingsConnections",
  components: { "fts-connections": FTSConnections },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    const profile = authorizationStore.getters.userProfile || {};
    return {
      key: String(profile.apiKey || ""),
      revealed: false,
      generating: false,
      copied: false,
    };
  },
  computed: {
    /** The user id half is not secret; the AppId half is. */
    masked() {
      const at = this.key.indexOf(":");
      return at >= 0 ? this.key.slice(0, at + 1) + "••••••••••••" : "••••••••••••";
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
  },
  methods: {
    async generate() {
      if (this.generating) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Generate a new API key",
        text: this.key
          ? "Your current key will be refused — within two hours everywhere — and every integration using it will need the new one."
          : "This creates a key that signs in as you, everywhere you have access.",
      });
      if (confirmed !== true) return;

      this.generating = true;
      try {
        const res = await authorizationStore.dispatch("getAPIKey");
        this.key = String((res && res.data) || "");
        this.revealed = true; // a key just made is one the person means to copy now
        authorizationStore.dispatch("setAPIKey", this.key);
      } catch (err) {
        this.$toasted.global.error(err, "A new key could not be generated. Your old key still works.");
      } finally {
        this.generating = false;
      }
    },
    async copy() {
      try {
        await navigator.clipboard.writeText(this.key);
        this.copied = true;
        window.setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        this.$toasted.global.error({}, "Copying was blocked — select the key and copy it by hand.");
      }
    },
  },
};
</script>
